<template>
  <span v-b-tooltip.hover="tooltip">
    <b-button
      :variant="variant"
      :disabled="disabled"
      size="sm"
      class="workforce-invite-btn"
      @click.stop="onClick"
    >
      {{ label }}
    </b-button>
  </span>
</template>

<script>
import { mapGetters } from 'vuex'

// Enabled only once the candidate's resume request has been accepted — the
// guard on POST /workforce-invitations requires that pairing server-side too
// (422 otherwise), this just avoids sending a request that's known to fail.
export default {
  name: 'WorkforceInviteButton',

  props: {
    resumeRequestStatus: {
      type: String,
      default: null,
    },
    workforceInvitationStatus: {
      type: String,
      default: null,
    },
    // The tradie applied to one of this company's jobs. That counts as
    // reaching out, so the invite is allowed without an accepted resume
    // request (the API applies the same rule).
    hasApplied: {
      type: Boolean,
      default: false,
    },
    // Already an accepted member of the company, even with no invitation row.
    isInWorkforce: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapGetters('auth', ['hasPermission', 'user']),

    // A member with no live invitation (none, or declined) reads as active.
    status() {
      const open = this.workforceInvitationStatus
      return this.isInWorkforce && (!open || open === 'declined')
        ? 'active'
        : open
    },

    canInvite() {
      return this.hasPermission('candidates.add_to_workforce')
    },

    // Free tier / free trial — `is_free` on /auth/me, same flag the router and
    // MainLayout gate on. The API applies a free bypass in
    // WorkforceInvitationService::applyToken() (no token consumed), so this
    // button drops all token wording for these companies.
    isFree() {
      return !!(this.user && this.user.is_free)
    },

    resumeAccepted() {
      return this.resumeRequestStatus === 'accepted' || this.hasApplied
    },

    label() {
      switch (this.status) {
        case 'pending':
          return 'Invitation Sent'
        case 'accepted':
          return 'Invitation Accepted'
        case 'token_pending':
          // No "token" wording for free tier / trial — they reach this state
          // only by choosing "not now" in the confirm, and it's still just
          // "click to finish adding them".
          return this.isFree ? 'Add to Workforce' : 'Token Pending'
        case 'active':
          return 'Added to Workforce'
        case 'declined':
          return 'Resend Invitation'
        default:
          return 'Invite to Workforce'
      }
    },

    variant() {
      if (this.status === 'active') return 'success'
      // Declined is actionable now (see isDeclined/onClick) — outline-primary
      // matches the other actionable state (needsToken) instead of the old
      // dead-end outline-danger, which read as final/unclickable.
      if (this.isDeclined) return 'outline-primary'
      if (this.needsToken) return 'outline-primary'
      if (this.status) return 'outline-secondary'
      return 'primary'
    },

    // Both states are actionable server-side (WorkforceInvitationService::
    // applyToken()/dismissToken() both accept 'accepted' or 'token_pending')
    // — 'accepted' is just the natural resting state right after the tradie
    // accepts, before anyone's explicitly snoozed it to 'token_pending'.
    // Previously this button went straight to disabled the moment any
    // status existed, which meant there was no way to apply the token from
    // here at all — the pending-token banner's "Review" link led to this
    // exact page with no working action on it.
    needsToken() {
      return this.status === 'accepted' || this.status === 'token_pending'
    },

    // A declined invitation isn't a dead end — the tradie can be re-invited,
    // same as if none had been sent yet. Kept as its own getter (rather than
    // folding into needsToken) since it reuses the plain 'invite' flow, not
    // apply-token.
    isDeclined() {
      return this.status === 'declined'
    },

    disabled() {
      if (this.needsToken) return !this.canInvite
      if (this.isDeclined) return !this.canInvite || !this.resumeAccepted
      return !this.canInvite || !this.resumeAccepted || !!this.status
    },

    tooltip() {
      if (!this.resumeAccepted && !this.status) {
        return {
          title:
            'Resume must be accepted before sending a workforce invitation.',
          disabled: false,
        }
      }
      if (this.isDeclined && !this.disabled) {
        return {
          title: 'They declined the previous invitation - click to resend.',
          disabled: false,
        }
      }
      if (this.needsToken && !this.disabled) {
        return {
          title: this.isFree
            ? 'Add them to your active workforce.'
            : 'Apply a token to add them to your workforce.',
          disabled: false,
        }
      }
      return { title: '', disabled: true }
    },
  },

  methods: {
    onClick() {
      if (this.disabled) return
      if (this.needsToken) {
        this.$emit('apply-token')
        return
      }
      // isDeclined falls through to here too — resending reuses the exact
      // same 'invite' event as the first send (WorkforceInvitationModal ->
      // sendWorkforceInvitation), since POST /workforce-invitations is the
      // one endpoint for both.
      this.$emit('invite')
    },
  },
}
</script>

<style lang="scss" scoped>
.workforce-invite-btn {
  // Was nowrap — same clipping issue as ResumeRequestButton.vue's
  // .resume-request-btn: this button also sits in ViewCandidate.vue's
  // 2-column grid, and longer labels ("Invitation Accepted", "Added to
  // Workforce") don't fit that narrower width on one line.
  white-space: normal;
}
</style>
